import { useState, useMemo, useCallback, useEffect, useRef, memo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Icon from '../common/Icon';
import AgGrid from '../common/AgGrid';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { GetListCostsData, UpdateListsCostsData } from '../../services/accountService';
import { AisScreenLoader } from '../../utils/AisScreenLoader';
import SuccessMessage from '../common/SuccessMessage';




const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    const numValue = parseFloat(value) || 0;
    const formatted = Math.abs(numValue).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    if (numValue < 0) return '-$' + formatted;
    return '$' + formatted;
};

const formatPercent = (value) => {
    if (value === null || value === undefined) return '0.00%';
    const numValue = parseFloat(value) || 0;
    if (numValue < 0) return '-' + Math.abs(numValue).toFixed(2) + '%';
    return numValue.toFixed(2) + '%';
};

const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return '';
    return Number(value).toLocaleString();
};

const formatDate = (value) => {
    if (!value) return '';
    try {
        const d = new Date(value);
        if (isNaN(d.getTime())) return '';
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${mm}/${dd}/${yyyy}`;
    } catch {
        return '';
    }
};

const rateTypeFromFlag = (flag) => {
    if (flag === true) return 'M';
    if (flag === false) return 'F';
    return 'F';
};


const MAX_FIELD_LENGTH = 20;


const ALPHANUMERIC_REGEX = /^[A-Za-z0-9]*$/;


const INTEGER_REGEX = /^\d*$/;


const DECIMAL_REGEX = /^\d*\.?\d*$/;


const sanitizeInput = (raw, validation, previous) => {
    if (raw === '' || raw === null || raw === undefined) return '';

   
    if (String(raw).length > MAX_FIELD_LENGTH) {
        return previous ?? '';
    }

    switch (validation) {
        case 'alphanumeric':
            return ALPHANUMERIC_REGEX.test(raw) ? raw : (previous ?? '');
        case 'integer':
            return INTEGER_REGEX.test(raw) ? raw : (previous ?? '');
        case 'decimal':
            return DECIMAL_REGEX.test(raw) ? raw : (previous ?? '');
        default:
            return raw;
    }
};


const mapApiToRow = (item) => {
    const clientEstRateType = rateTypeFromFlag(item.isPreInvoiceCostPerThousand);
    const estRateType = rateTypeFromFlag(item.isEstimatedCostPerThousand);
    const actRateType = rateTypeFromFlag(item.isActualCostPerThousand);

    return {
        listId: item.mailDetailID,
        id: item.mailDetailID,
        jobCostID: item.jobCostID,
        mailDetailID: item.mailDetailID,
        listID: item.listID,
        listTypeID: item.listTypeID,

        mailCode: item.mailCode || '',
        listName: item.listName || '',
        select: item.listSegment || '',
        mailDate: formatDate(item.mailDate),
        orderNumber: item.orderNumber || '',
        orderQuantity: item.orderQuantity,

        clientEstInvNumber: item.preInvoiceNumber || '',
        clientEstimate: item.preInvoiceCost,
        clientEstRateType,
        totalClientEstCost: item.totalClientEstCost,

        estQuantity: item.estimatedQuantity,
        estCost: item.estimatedCost,
        estRateType,
        totalEstCost: item.totalEstCost,

        actQuantity: item.actualQuantity,
        actCost: item.actualCost,
        actRateType,
        totalActCost: item.totalActCost,

        isCostFinal: !!item.isCostFinal,
        status: item.isCostFinal ? 'Yes' : 'No',
        variance: item.costVariance,
        variancePercent: item.costVariancePercent,
    };
};


const EditableInputRenderer = memo(({
    value,
    id,
    field,
    type = 'text',
    isCurrency = false,
    placeholder = '',
    isEditMode,
    onFieldChange,
    validation,
    maxLength = MAX_FIELD_LENGTH,
}) => {
   
    const [localValue, setLocalValue] = useState(value ?? '');

    useEffect(() => {
        setLocalValue(value ?? '');
    }, [value]);

    if (!isEditMode) {
        if (isCurrency) {
            const displayValue = (value === null || value === undefined) ? 0 : value;
            return <span>${Number(displayValue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
        }
        if (type === 'number') {
            const displayValue = (value === null || value === undefined) ? 0 : value;
            return <span>{Number(displayValue).toLocaleString()}</span>;
        }
        return <span>{value || ''}</span>;
    }

   
    return (
        <input
            type="text"
            value={localValue ?? ''}
            placeholder={placeholder}
            maxLength={maxLength}
            onKeyDown={(e) => {
                
                const blockedAlways = ['e', 'E', '+'];
                if (validation === 'integer' && (blockedAlways.includes(e.key) || e.key === '.' || e.key === '-')) {
                    e.preventDefault();
                }
                if (validation === 'decimal' && (blockedAlways.includes(e.key) || e.key === '-')) {
                    e.preventDefault();
                }
            }}
            onChange={(e) => {
                const raw = e.target.value;
                const cleaned = sanitizeInput(raw, validation, localValue);
                setLocalValue(cleaned);
            }}
            onBlur={() => {
                let finalValue = localValue;

                if (validation === 'integer') {
                    finalValue = (localValue === '' || localValue === null || localValue === undefined)
                        ? 0
                        : (parseInt(localValue, 10) || 0);
                } else if (validation === 'decimal') {
                    finalValue = (localValue === '' || localValue === null || localValue === undefined)
                        ? 0
                        : (parseFloat(localValue) || 0);
                } else if (type === 'number') {
                    finalValue = (localValue === '' || localValue === null || localValue === undefined)
                        ? 0
                        : (parseFloat(localValue) || 0);
                } else {
                   
                    finalValue = String(localValue ?? '').trim();
                }

                if (String(finalValue) !== String(value ?? '')) {
                    onFieldChange(id, field, finalValue);
                }
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            style={{
                width: '100%',
                padding: '6px 8px',
                border: '1px solid #007bff',
                borderRadius: '4px',
                fontSize: '13px',
                fontFamily: 'inherit',
                textAlign: isCurrency ? 'right' : 'left',
                backgroundColor: 'white',
                outline: 'none',
            }}
            inputMode={
                validation === 'integer' ? 'numeric'
                    : validation === 'decimal' ? 'decimal'
                        : 'text'
            }
            title={`Max ${maxLength} characters`}
        />
    );
});
EditableInputRenderer.displayName = 'EditableInputRenderer';


const MFRateDropdown = memo(({ value, id, field, isEditMode, onFieldChange }) => {
    let currentValue = 'F';
    if (value === true || value === 'M') {
        currentValue = 'M';
    } else if (value === false || value === 'F') {
        currentValue = 'F';
    }

    return (
        <select
            value={currentValue}
            onChange={(e) => onFieldChange(id, field, e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            disabled={!isEditMode}
            style={{
                width: '80px',
                padding: '6px 8px',
                border: `1px solid ${isEditMode ? '#007bff' : '#d0d0d0'}`,
                borderRadius: '4px',
                fontSize: '13px',
                fontFamily: 'inherit',
                fontWeight: '500',
                textAlign: 'center',
                cursor: isEditMode ? 'pointer' : 'default',
                backgroundColor: isEditMode ? '#ffffff' : '#f8f9fa',
                outline: 'none',
            }}
        >
            <option value="M">M</option>
            <option value="F">F</option>
        </select>
    );
});
MFRateDropdown.displayName = 'MFRateDropdown';

// ─── Status Dropdown (BackendCostsDetails pattern) ────────────────────────────
const StatusDropdownCell = memo(({ data, isEditMode, onFieldChange }) => {
    const currentValue = data?.status || 'No';
    const id = data?.id ?? data?.listId;

    if (!id || data?.isTotalsRow) {
        return <span></span>;
    }

    return (
        <select
            value={currentValue}
            onChange={(e) => {
                e.stopPropagation();
                const isFinal = e.target.value === 'Yes';
                onFieldChange(id, 'isCostFinal', isFinal);
                onFieldChange(id, 'status', e.target.value);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            disabled={!isEditMode}
            style={{
                width: '100%',
                padding: '6px 10px',
                border: `1px solid ${isEditMode ? '#007bff' : '#d0d0d0'}`,
                borderRadius: '4px',
                backgroundColor: isEditMode ? '#ffffff' : '#f8f9fa',
                cursor: isEditMode ? 'pointer' : 'default',
                fontSize: '13px',
                fontFamily: 'inherit',
                fontWeight: '500',
                outline: 'none',
            }}
        >
            <option value="Yes">Yes</option>
            <option value="No">No</option>
        </select>
    );
});
StatusDropdownCell.displayName = 'StatusDropdownCell';


const LIST_TYPE_ID_TO_LABEL = { 1: 'E', 2: 'R', 3: 'H' };
const LIST_TYPE_LABEL_TO_ID = { E: 1, R: 2, H: 3 };

const RExHDropdown = memo(({ value, id, field, isEditMode, onFieldChange }) => {
   
    const currentLabel = LIST_TYPE_ID_TO_LABEL[value] || '';

    return (
        <select
            value={currentLabel}
            onChange={(e) => {
                const label = e.target.value;
                const newId = label === '' ? null : LIST_TYPE_LABEL_TO_ID[label];
                onFieldChange(id, field, newId);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            disabled={!isEditMode}
            style={{
                width: '90px',
                padding: '6px 8px',
                border: `1px solid ${isEditMode ? '#007bff' : '#d0d0d0'}`,
                borderRadius: '4px',
                fontSize: '13px',
                fontFamily: 'inherit',
                fontWeight: '500',
                textAlign: 'center',
                cursor: isEditMode ? 'pointer' : 'default',
                backgroundColor: isEditMode ? '#ffffff' : '#f8f9fa',
                outline: 'none',
            }}
        >
            <option value=""></option>
            <option value="E">E</option>
            <option value="R">R</option>
            <option value="H">H</option>
        </select>
    );
});
RExHDropdown.displayName = 'RExHDropdown';


const EmptyValueRenderer = (params) => {
    const value = params.value;
    if (!value || value === '' || value === null || value === undefined) {
        return <span style={{ color: '#999', fontStyle: 'italic' }}></span>;
    }
    return <span>{value}</span>;
};

const VarianceRenderer = (params) => {
    const variance = params.value;
    if (variance === null || variance === undefined || variance === '') {
        return <span>$0.00</span>;
    }
    const symbol = variance < 0 ? '-' : '';
    return (
        <span>
            {symbol}${Math.abs(Number(variance)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
    );
};

const VariancePercentRenderer = (params) => {
    const variancePercent = params.value;
    if (variancePercent === null || variancePercent === undefined || variancePercent === '') {
        return <span>0.00%</span>;
    }
    const symbol = variancePercent < 0 ? '-' : '';
    return (
        <span>
            {symbol}{Math.abs(Number(variancePercent)).toFixed(2)}%
        </span>
    );
};


const ListsDetails = () => {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [saveError, setSaveError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [messageType, setMessageType] = useState('success');

    const activeJobId = jobId || location.state?.jobId;
    const jobCode = location.state?.jobCode;
    const clientName = location.state?.clientName;

   
    const [listsData, setListsData] = useState([]);
   
    const [savedListsData, setSavedListsData] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [showDropdownMenu, setShowDropdownMenu] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    
    const [filteredData, setFilteredData] = useState(null);

    const [showCostTypeDropdown, setShowCostTypeDropdown] = useState(false);
    const [selectedCostType, setSelectedCostType] = useState('lists');

    const [savingChanges, setSavingChanges] = useState(false);
    const [originalData, setOriginalData] = useState([]);

   
    const agGridRef = useRef(null);

  
    const gridApiRef = useRef(null);

    
    const menuDropdownRef = useRef(null);

    const clientId = location.state?.clientId;

    const [navigationSource, setNavigationSource] = useState(() => {
        if (location.state?.source) return location.state.source;
        const stored = sessionStorage.getItem('jobDetailsSource');
        return stored || 'job-module';
    });

    const [isAllClientsFlow, setIsAllClientsFlow] = useState(() => {
        if (location.state?.isAllClients !== undefined) {
            return Boolean(location.state.isAllClients);
        }
        return sessionStorage.getItem('isAllClientsFlow') === 'true';
    });

    const [cameFromIntegration, setCameFromIntegration] = useState(() => {
        if (location.state?.fromIntegration !== undefined) {
            return Boolean(location.state.fromIntegration);
        }
        return sessionStorage.getItem('cameFromIntegration') === 'true';
    });

    const [reportsContext, setReportsContext] = useState(() => {
        if (location.state?.fromReports) {
            return {
                fromReports: true,
                reportFilters: location.state.reportFilters || {},
                selectedClientId: location.state.selectedClientId || [],
                reportType: location.state.reportType || '',
                rowData: location.state.rowData || []
            };
        }
        const stored = sessionStorage.getItem('reportsContext');
        return stored ? JSON.parse(stored) : null;
    });

    const showReportsCrumb =

        reportsContext?.fromReports &&

        navigationSource === 'reports';

    useEffect(() => {
        if (location.state?.fromReports) {
            const ctx = {
                fromReports: true,
                reportFilters: location.state.reportFilters || {},
                selectedClientId: location.state.selectedClientId || [],
                reportType: location.state.reportType || '',
                rowData: location.state.rowData || []
            };
            sessionStorage.setItem('reportsContext', JSON.stringify(ctx));
            setReportsContext(ctx);
        }
    }, [location.state]);


    useEffect(() => {
        if (location.state?.source) {
            sessionStorage.setItem('jobDetailsSource', location.state.source);
            setNavigationSource(location.state.source);
        }

        if (location.state?.isAllClients !== undefined) {
            sessionStorage.setItem(
                'isAllClientsFlow',
                String(Boolean(location.state.isAllClients))
            );
            setIsAllClientsFlow(Boolean(location.state.isAllClients));
        }
    }, [location.state]);

    const showClientSelectCrumb =
        navigationSource === 'dashboard' ||
        navigationSource === 'client-dashboard' ||
        isAllClientsFlow;

    const showClientNameCrumb =
        (navigationSource === 'dashboard' ||
            navigationSource === 'client-dashboard') &&
        !isAllClientsFlow;



    // ─── Fetch Lists ──────────────────────────────────────────────────────────
    const fetchLists = useCallback(async () => {
        if (!activeJobId) return;
        try {
            setLoading(true);
            setError(null);
            const data = await GetListCostsData(activeJobId);
            const mapped = Array.isArray(data) ? data.map(mapApiToRow) : [];
            setListsData(mapped);
           
            setSavedListsData(mapped);
        } catch (err) {
            console.error('Failed to load list costs:', err);
           
            setListsData([]);
            setSavedListsData([]);
        } finally {
            setLoading(false);
        }
    }, [activeJobId]);

    useEffect(() => {
        fetchLists();
    }, [fetchLists]);

   
    useEffect(() => {
        setFilteredData(null);
    }, [listsData.length]);

  
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuDropdownRef.current && !menuDropdownRef.current.contains(event.target)) {
                setShowDropdownMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

   
    const handleFieldChange = useCallback((id, field, value) => {
        setListsData(prev =>
            prev.map(row => {
                if (row.listId !== id && row.id !== id) return row;
                let updated = { ...row, [field]: value };

                
                if (field === 'isCostFinal') {
                    updated.status = value ? 'Yes' : 'No';
                }
                if (field === 'status') {
                    updated.isCostFinal = value === 'Yes';
                }

                return updated;
            })
        );
    }, []);

   
    const calculateTotals = useCallback((data) => {
        if (!data || data.length === 0) return {
            orderQuantity: 0,
            clientEstimate: 0, totalClientEstCost: 0,
            estQuantity: 0, estCost: 0, totalEstCost: 0,
            actQuantity: 0, actCost: 0, totalActCost: 0,
            variance: 0, variancePercent: 0,
        };

        let orderQuantity = 0;
        let clientEstimate = 0, totalClientEstCost = 0;
        let estQuantity = 0, estCost = 0, totalEstCost = 0;
        let actQuantity = 0, actCost = 0, totalActCost = 0;

        data.forEach(row => {
            if (row.isTotalsRow) return;
            orderQuantity += parseFloat(row.orderQuantity) || 0;
            clientEstimate += parseFloat(row.clientEstimate) || 0;
            totalClientEstCost += parseFloat(row.totalClientEstCost) || 0;
            estQuantity += parseFloat(row.estQuantity) || 0;
            estCost += parseFloat(row.estCost) || 0;
            totalEstCost += parseFloat(row.totalEstCost) || 0;
            actQuantity += parseFloat(row.actQuantity) || 0;
            actCost += parseFloat(row.actCost) || 0;
            totalActCost += parseFloat(row.totalActCost) || 0;
        });

        const variance = totalActCost - totalClientEstCost;
        const variancePercent = totalActCost !== 0
            ? ((totalActCost - totalClientEstCost) * 100) / totalActCost
            : 0;

        const r = (v) => Math.round(v * 100) / 100;
        return {
            orderQuantity: r(orderQuantity),
            clientEstimate: r(clientEstimate),
            totalClientEstCost: r(totalClientEstCost),
            estQuantity: r(estQuantity),
            estCost: r(estCost),
            totalEstCost: r(totalEstCost),
            actQuantity: r(actQuantity),
            actCost: r(actCost),
            totalActCost: r(totalActCost),
            variance: r(variance),
            variancePercent: r(variancePercent),
        };
    }, []);

  
    const displayData = filteredData !== null ? filteredData : listsData;

   
    const displayTotals = useMemo(
        () => calculateTotals(savedListsData),
        [savedListsData, calculateTotals]
    );

   
    const handleFilterChanged = useCallback((params) => {
        const api = params?.api || gridApiRef.current;
        if (!api || typeof api.forEachNodeAfterFilter !== 'function') return;

        const allCount = listsData.filter(r => !r.isTotalsRow).length;
        const rows = [];
        api.forEachNodeAfterFilter((node) => {
            const d = node.data;
            if (d && !d.isTotalsRow) rows.push(d);
        });

        if (rows.length < allCount) {
            setFilteredData(rows);
        } else {
            setFilteredData(null);
        }
    }, [listsData]);

   
    const toggleDropdownMenu = () => setShowDropdownMenu(prev => !prev);

    const mapRowToApiPayload = (row) => ({
        jobCostID: row.jobCostID ?? null,
        mailDetailID: row.mailDetailID,
        listTypeID: row.listTypeID ?? null,

        preInvoiceNumber: row.clientEstInvNumber || '',
        preInvoiceCost: Number(row.clientEstimate) || 0,
        orderNumber: row.orderNumber || '',
        orderQuantity: Number(row.orderQuantity) || 0,
        isPreInvoiceCostPerThousand: row.clientEstRateType === 'M',

        estimatedQuantity: Number(row.estQuantity) || 0,
        estimatedCost: Number(row.estCost) || 0,
        isEstimatedCostPerThousand: row.estRateType === 'M',

        actualQuantity: Number(row.actQuantity) || 0,
        actualCost: Number(row.actCost) || 0,
        isActualCostPerThousand: row.actRateType === 'M',

        isCostFinal: Boolean(row.isCostFinal),
    });

    const handleSaveChanges = async () => {
        if (!activeJobId) {
            setSaveError('Missing job id — cannot save.');
            return;
        }
        try {
            setSavingChanges(true);
            setSaveError(null);

            const payload = listsData
                .filter(r => !r.isTotalsRow)
                .map(mapRowToApiPayload);

            const response = await UpdateListsCostsData(activeJobId, payload);

           
            await fetchLists();

            setIsEditMode(false);
            setOriginalData([]);

            const updatedCount = Array.isArray(response) ? response.length : null;
            setMessageType('success');
            setSuccessMessage(
                updatedCount !== null
                    ? `Saved successfully. ${updatedCount} record${updatedCount === 1 ? '' : 's'} updated.`
                    : 'Changes saved successfully.'
            );
        } catch (err) {
            if (err?.response?.status === 400) {
                const warningMsg =
                    err?.response?.data?.message ||
                    err?.response?.data?.error ||
                    'Duplicate or invalid data in request.';
                setMessageType('error');
                setSuccessMessage(warningMsg);
            } else {
                const apiMsg =
                    err?.response?.data?.message ||
                    err?.response?.data?.error ||
                    err?.message ||
                    'Failed to save changes.';

                setSuccessMessage(apiMsg || 'Failed to save changes');
                setMessageType('error');

            }
        } finally {
            setSavingChanges(false);
        }
    };

    const handleCancelChanges = () => {
        // Revert the live buffer to whatever the snapshot has
        if (savedListsData.length > 0) {
            setListsData(savedListsData.map(r => ({ ...r })));
        } else if (originalData.length > 0) {
            setListsData(originalData);
        }
        setOriginalData([]);
        setIsEditMode(false);
        setSavingChanges(false);
    };

    const handleManageComponents = () => {
        setOriginalData(listsData.map(r => ({ ...r })));
        setIsEditMode(true);
    };

  
    const totalsRow = useMemo(() => ({
        mailCode: '',
        listName: 'TOTAL',
        select: '',
        mailDate: '',
        orderNumber: '',
        orderQuantity: displayTotals.orderQuantity,
        listTypeID: null,
        clientEstInvNumber: '',
        clientEstimate: displayTotals.clientEstimate,
        clientEstRateType: '',
        totalClientEstCost: displayTotals.totalClientEstCost,
        estQuantity: displayTotals.estQuantity,
        estCost: displayTotals.estCost,
        estRateType: '',
        totalEstCost: displayTotals.totalEstCost,
        actQuantity: displayTotals.actQuantity,
        actCost: displayTotals.actCost,
        actRateType: '',
        totalActCost: displayTotals.totalActCost,
        isCostFinal: false,
        status: '',
        variance: displayTotals.variance,
        variancePercent: displayTotals.variancePercent,
        isTotalsRow: true,
    }), [displayTotals]);

   
    const handleCostTypeChange = (type) => {
        setSelectedCostType(type);
        setShowCostTypeDropdown(false);
        const statePayload = { jobId: activeJobId, jobCode, clientName };
        switch (type) {
            case 'postage': navigate('/postage-details', { state: statePayload }); break;
            case 'package': navigate('/package-costs-details', { state: statePayload }); break;
            case 'backend': navigate('/backend-costs-details', { state: statePayload }); break;
            case 'lists': navigate('/lists-details', { state: statePayload }); break;
            default: break;
        }
    };

    const handleJobDetailsClick = (e) => {
        e.preventDefault();
        navigate(`/job-details/${jobCode}`, {
            state: {
                jobId: activeJobId,
                jobCode,
                clientName,
                source: navigationSource,
                isAllClients: isAllClientsFlow,
                ...(cameFromIntegration ? { fromIntegration: true } : {}),
                ...(reportsContext?.fromReports
                    ? {
                        fromReports: true,
                        reportFilters: reportsContext.reportFilters,
                        selectedClientId: reportsContext.selectedClientId,
                        reportType: reportsContext.reportType,
                        rowData: reportsContext.rowData
                    }
                    : {})
            }
        });
    };

    const handleJobListsClick = (e) => {
        e.preventDefault();

        navigate('/job-module', {
            state: {
                clientId: clientId,
                source: navigationSource,
                isAllClients: isAllClientsFlow
            }
        });
    };

   
    const columnDefs = useMemo(() => [
        
        {
            field: 'mailCode',
            headerName: 'Mail Code',
            width: 180, minWidth: 180, maxWidth: 180,
            suppressSizeToFit: true,
            pinned: 'left',
            lockPinned: true,
            filter: 'agTextColumnFilter',
            sortable: true,
            cellRenderer: EmptyValueRenderer,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) return { fontWeight: 'bold', paddingLeft: '15px' };
                return { fontWeight: '500' };
            }
        },

        {
            field: 'listName',
            headerName: 'List Name',
            width: 250,
            minWidth: 300,
            maxWidth: 300,
            suppressSizeToFit: true,
            filter: 'agTextColumnFilter',
            sortable: true,

            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span style={{ fontWeight: 'bold' }}>TOTAL</span>;
                }

                return (
                    <span
                        style={{
                            display: 'block',
                            width: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {params.value || ''}
                    </span>
                );
            },

            tooltipValueGetter: (params) =>
                params.data?.isTotalsRow ? '' : (params.value || ''),

            cellStyle: (params) => {
                const truncate = {
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                };

                if (params.data?.isTotalsRow) {
                    return {
                        ...truncate,
                        fontWeight: 'bold',
                        paddingLeft: '15px'
                    };
                }

                return {
                    ...truncate,
                    fontWeight: '500'
                };
            }
        },

        {
            field: 'select',
            headerName: 'List Select',
            minWidth: 300,
            maxWidth: 300,
            flex: 1.5,
            filter: 'agTextColumnFilter',
            sortable: true,

            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) return <span></span>;

                return (
                    <span
                        style={{
                            display: 'block',
                            width: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {params.value || ''}
                    </span>
                );
            },

            tooltipValueGetter: (params) =>
                params.data?.isTotalsRow ? '' : (params.value || ''),

            cellStyle: () => ({
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis'
            })
        },
       
        {
            field: 'mailDate',
            headerName: 'Mail Date',
              comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
            minWidth: 180, maxWidth: 180,
            filter: 'agTextColumnFilter', sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) return <span></span>;
                return <span>{params.value || ''}</span>;
            },
        },
       
        {
            field: 'orderNumber',
            headerName: 'Order Number',
            minWidth: 180, maxWidth: 180,
            filter: 'agTextColumnFilter', sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) return <span></span>;
                return (
                    <EditableInputRenderer
                        value={params.value}
                        id={params.data?.id ?? params.data?.listId}
                        field="orderNumber"
                        type="text"
                        placeholder="Order #"
                        isEditMode={isEditMode}
                        onFieldChange={handleFieldChange}
                        validation="alphanumeric"
                        maxLength={20}
                    />
                );
            },
        },
       
        {
            field: 'orderQuantity',
            headerName: 'Order Quantity',
            minWidth: 180, maxWidth: 180, flex: 0.8,
            filter: 'agTextColumnFilter', sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span style={{ fontWeight: 'bold' }}>{formatNumber(params.value)}</span>;
                }
                return (
                    <EditableInputRenderer
                        value={params.value}
                        id={params.data?.id ?? params.data?.listId}
                        field="orderQuantity"
                        type="number"
                        placeholder="0"
                        isEditMode={isEditMode}
                        onFieldChange={handleFieldChange}
                        validation="integer"
                        maxLength={20}
                    />
                );
            },
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) return { fontWeight: 'bold', textAlign: 'right', paddingRight: '15px' };
                return { textAlign: 'right' };
            }
        },
       
        {
            field: 'listTypeID',
            headerName: 'R/E/H',
            minWidth: 150, maxWidth: 150,
            filter: 'agTextColumnFilter', sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) return <span></span>;
                return (
                    <RExHDropdown
                        value={params.value}
                        id={params.data?.id ?? params.data?.listId}
                        field="listTypeID"
                        isEditMode={isEditMode}
                        onFieldChange={handleFieldChange}
                    />
                );
            },
            cellStyle: { textAlign: 'center' },
        },
       
        {
            field: 'clientEstInvNumber',
            headerName: 'Client Est. Inv. #',
            minWidth: 200, maxWidth: 200,
            filter: 'agTextColumnFilter', sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) return <span></span>;
                return (
                    <EditableInputRenderer
                        value={params.value}
                        id={params.data?.id ?? params.data?.listId}
                        field="clientEstInvNumber"
                        type="text"
                        placeholder="Invoice #"
                        isEditMode={isEditMode}
                        onFieldChange={handleFieldChange}
                        validation="alphanumeric"
                        maxLength={20}
                    />
                );
            },
        },
       
        {
            field: 'clientEstimate',
            headerName: 'Client Estimate',
            minWidth: 200, maxWidth: 200, flex: 1,
            filter: 'agTextColumnFilter', sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span></span>;
                }
                return (
                    <EditableInputRenderer
                        value={params.value}
                        id={params.data?.id ?? params.data?.listId}
                        field="clientEstimate"
                        type="number"
                        isCurrency={true}
                        placeholder="0.00"
                        isEditMode={isEditMode}
                        onFieldChange={handleFieldChange}
                        validation="decimal"
                        maxLength={20}
                    />
                );
            },
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) return { fontWeight: 'bold', textAlign: 'right', paddingRight: '15px' };
                return { textAlign: 'right' };
            },
        },
       
        {
            field: 'clientEstRateType',
            headerName: 'M/F',
            minWidth: 120, maxWidth: 180,
            filter: 'agTextColumnFilter', sortable: false,
            editable: false,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) return <span></span>;
                return (
                    <MFRateDropdown
                        value={params.value}
                        id={params.data?.id ?? params.data?.listId}
                        field="clientEstRateType"
                        isEditMode={isEditMode}
                        onFieldChange={handleFieldChange}
                    />
                );
            },
            cellStyle: { textAlign: 'center' },
        },
        
        {
            field: 'totalClientEstCost',
            headerName: 'Total Client Est. Cost',
            minWidth: 250, maxWidth: 250, flex: 1,
            filter: 'agTextColumnFilter', sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) return <span style={{ fontWeight: 'bold' }}>{formatCurrency(params.value)}</span>;
                return <span>{formatCurrency(params.value ?? 0)}</span>;
            },
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) return { fontWeight: 'bold', textAlign: 'right', paddingRight: '15px' };
                return { textAlign: 'right' };
            }
        },
       
        {
            field: 'estQuantity',
            headerName: 'Est. Mail Qty',
            minWidth: 170, maxWidth: 170, flex: 1,
            filter: 'agTextColumnFilter', sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span style={{ fontWeight: 'bold' }}>{formatNumber(params.value)}</span>;
                }
                return (
                    <EditableInputRenderer
                        value={params.value}
                        id={params.data?.id ?? params.data?.listId}
                        field="estQuantity"
                        type="number"
                        placeholder="0"
                        isEditMode={isEditMode}
                        onFieldChange={handleFieldChange}
                        validation="integer"
                        maxLength={20}
                    />
                );
            },
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) return { fontWeight: 'bold', textAlign: 'right', paddingRight: '15px' };
                return { textAlign: 'right' };
            },
        },
        
        {
            field: 'estCost',
            headerName: 'Est. Cost',
            minWidth: 140, maxWidth: 190, flex: 1,
            filter: 'agTextColumnFilter', sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span></span>;
                }
                return (
                    <EditableInputRenderer
                        value={params.value}
                        id={params.data?.id ?? params.data?.listId}
                        field="estCost"
                        type="number"
                        isCurrency={true}
                        placeholder="0.00"
                        isEditMode={isEditMode}
                        onFieldChange={handleFieldChange}
                        validation="decimal"
                        maxLength={20}
                    />
                );
            },
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) return { fontWeight: 'bold', textAlign: 'right', paddingRight: '15px' };
                return { textAlign: 'right' };
            },
        },
       
        {
            field: 'estRateType',
            headerName: 'M/F',
            minWidth: 120, maxWidth: 180,
            filter: 'agTextColumnFilter', sortable: false,
            editable: false,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) return <span></span>;
                return (
                    <MFRateDropdown
                        value={params.value}
                        id={params.data?.id ?? params.data?.listId}
                        field="estRateType"
                        isEditMode={false}
                        onFieldChange={handleFieldChange}
                    />
                );
            },
            cellStyle: { textAlign: 'center' },
        },
       
        {
            field: 'totalEstCost',
            headerName: 'Total Est. Cost',
            minWidth: 180, maxWidth: 180, flex: 1,
            filter: 'agTextColumnFilter', sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) return <span style={{ fontWeight: 'bold' }}>{formatCurrency(params.value)}</span>;
                return <span>{formatCurrency(params.value ?? 0)}</span>;
            },
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) return { fontWeight: 'bold', textAlign: 'right', paddingRight: '15px' };
                return { textAlign: 'right' };
            }
        },
       
      {
    field: 'actQuantity',
    headerName: 'Final. Mail Qty',
    minWidth: 180, maxWidth: 180, flex: 1,
    filter: 'agTextColumnFilter', sortable: true,
    cellRenderer: (params) => {
        if (params.data?.isTotalsRow) {
            return <span style={{ fontWeight: 'bold' }}>{formatNumber(params.value)}</span>;
        }
        return <span>{formatNumber(params.value ?? 0)}</span>;
    },
    cellStyle: (params) => {
        if (params.data?.isTotalsRow) return { fontWeight: 'bold', textAlign: 'right', paddingRight: '15px' };
        return { textAlign: 'right' };
    },
},
       
        {
            field: 'actCost',
            headerName: 'Act. Cost',
            minWidth: 190, maxWidth: 190, flex: 1,
            filter: 'agTextColumnFilter', sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span></span>;
                }
                return (
                    <EditableInputRenderer
                        value={params.value}
                        id={params.data?.id ?? params.data?.listId}
                        field="actCost"
                        type="number"
                        isCurrency={true}
                        placeholder="0.00"
                        isEditMode={isEditMode}
                        onFieldChange={handleFieldChange}
                        validation="decimal"
                        maxLength={20}
                    />
                );
            },
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) return { fontWeight: 'bold', textAlign: 'right', paddingRight: '15px' };
                return { textAlign: 'right' };
            },
        },
       
        {
            field: 'actRateType',
            headerName: 'M/F',
            minWidth: 120, maxWidth: 180,
            filter: 'agTextColumnFilter', sortable: false,
            editable: false,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) return <span></span>;
                return (
                    <MFRateDropdown
                        value={params.value}
                        id={params.data?.id ?? params.data?.listId}
                        field="actRateType"
                        isEditMode={false}
                        onFieldChange={handleFieldChange}
                    />
                );
            },
            cellStyle: { textAlign: 'center' },
        },
        
        {
            field: 'totalActCost',
            headerName: 'Total Act. Cost',
            minWidth: 250, maxWidth: 250, flex: 1,
            filter: 'agTextColumnFilter', sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) return <span style={{ fontWeight: 'bold' }}>{formatCurrency(params.value)}</span>;
                return <span>{formatCurrency(params.value ?? 0)}</span>;
            },
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) return { fontWeight: 'bold', textAlign: 'right', paddingRight: '15px' };
                return { textAlign: 'right' };
            }
        },
        
        {
            field: 'status',
            headerName: 'Status',
            minWidth: 150, maxWidth: 150,
            filter: 'agTextColumnFilter', sortable: false,
            cellRenderer: (params) => (
                <StatusDropdownCell
                    data={params.data}
                    isEditMode={isEditMode}
                    onFieldChange={handleFieldChange}
                />
            ),
        },
       
        {
            field: 'variance',
            headerName: 'Cost Variance',
            minWidth: 180, maxWidth: 180, flex: 1,
            filter: 'agTextColumnFilter', sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span style={{ fontWeight: 'bold' }}>{formatCurrency(params.value)}</span>;
                }
                return <VarianceRenderer {...params} />;
            },
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) return { fontWeight: 'bold', textAlign: 'right', paddingRight: '15px' };
                return { textAlign: 'right' };
            }
        },
       
        {
            field: 'variancePercent',
            headerName: 'Cost Variance %',
            minWidth: 350, maxWidth: 350, flex: 1,
            filter: 'agTextColumnFilter', sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span style={{ fontWeight: 'bold' }}>{formatPercent(params.data?.variancePercent ?? 0)}</span>;
                }
                return <VariancePercentRenderer {...params} />;
            },
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) return { fontWeight: 'bold', textAlign: 'right', paddingRight: '15px' };
                return { textAlign: 'right' };
            }
        },
    ], [isEditMode, handleFieldChange]);

  
    const getRowId = useCallback((params) => {
        if (params.data?.isTotalsRow) return 'totals-row';
        if (params.data?.id) return String(params.data.id);
        if (params.data?.listId) return String(params.data.listId);
        return undefined;
    }, []);

  
    const formatValueForField = (field, value) => {
        const currencyFields = [
            'clientEstimate', 'totalClientEstCost', 'estCost', 'totalEstCost',
            'actCost', 'totalActCost', 'variance'
        ];
        const numericFields = ['orderQuantity', 'estQuantity', 'actQuantity'];
        const rateTypeFields = ['clientEstRateType', 'estRateType', 'actRateType'];

        if (field === 'listTypeID') return LIST_TYPE_ID_TO_LABEL[value] || '';
        if (field === 'status') return value || '';
        if (field === 'isCostFinal') return value === true ? 'Yes' : 'No';

        if (field === 'variancePercent') {
            if (value === null || value === undefined || value === '') return '';
            return formatPercent(value);
        }
        if (currencyFields.includes(field)) {
            if (value === null || value === undefined || value === '') return '';
            return formatCurrency(value);
        }
        if (numericFields.includes(field)) {
            if (value === null || value === undefined || value === '') return '';
            const n = Number(value);
            return isNaN(n) ? String(value) : n.toLocaleString();
        }
        if (rateTypeFields.includes(field)) {
            return value === true || value === 'M' ? 'M' : (value === false || value === 'F' ? 'F' : (value || ''));
        }
        if (value === null || value === undefined) return '';
        return String(value);
    };

   
    const allExportColumns = useMemo(() => ([
        { field: 'mailCode', headerName: 'Mail Code' },
        { field: 'listName', headerName: 'List Name' },
        { field: 'select', headerName: 'List Select' },
        { field: 'mailDate', headerName: 'Mail Date' },
        { field: 'orderNumber', headerName: 'Order Number' },
        { field: 'orderQuantity', headerName: 'Order Quantity' },
        { field: 'listTypeID', headerName: 'R/E/H' },
        { field: 'clientEstInvNumber', headerName: 'Client Est. Inv. #' },
        { field: 'clientEstimate', headerName: 'Client Estimate' },
        { field: 'clientEstRateType', headerName: 'M/F (Client Est.)' },
        { field: 'totalClientEstCost', headerName: 'Total Client Est. Cost' },
        { field: 'estQuantity', headerName: 'Est. Mail Qty' },
        { field: 'estCost', headerName: 'Est. Cost' },
        { field: 'estRateType', headerName: 'M/F (Est.)' },
        { field: 'totalEstCost', headerName: 'Total Est. Cost' },
        { field: 'actQuantity', headerName: 'Final. Mail Qty' },
        { field: 'actCost', headerName: 'Act. Cost' },
        { field: 'actRateType', headerName: 'M/F (Act.)' },
        { field: 'totalActCost', headerName: 'Total Act. Cost' },
        { field: 'status', headerName: 'Status' },
        { field: 'variance', headerName: 'Cost Variance' },
        { field: 'variancePercent', headerName: 'Cost Variance %' }
    ]), []);

   
    const getExportColumns = useCallback(() => {
        if (agGridRef.current && typeof agGridRef.current.getVisibleColumns === 'function') {
            const visible = agGridRef.current.getVisibleColumns();
            if (Array.isArray(visible)) {
                return visible.filter((c) => c.field && c.field !== 'action');
            }
        }
        return allExportColumns;
    }, [allExportColumns]);

   
    const totalFieldsSet = useMemo(() => new Set([
        'orderQuantity', 'clientEstimate', 'totalClientEstCost', 'estQuantity',
        'estCost', 'totalEstCost', 'actQuantity', 'actCost', 'totalActCost',
        'variance', 'variancePercent'
    ]), []);

    
    const getExportableRowsFrom = useCallback((source) => {
        return (source || []).filter(item => !item.isTotalsRow);
    }, []);

   
    const getExportRows = useCallback(() => {
        const allRows = getExportableRowsFrom(listsData);

        if (allRows.length === 0) {
            return { rows: [], scope: 'all' };
        }

        if (filteredData !== null) {
            return { rows: getExportableRowsFrom(filteredData), scope: 'filtered' };
        }

        return { rows: allRows, scope: 'all' };
    }, [listsData, filteredData, getExportableRowsFrom]);

  
    const exportToExcel = () => {
        const { rows: validData } = getExportRows();

        // ✅ NO DATA CASE
        if (!validData || validData.length === 0) {
            setSuccessMessage('No data to export');
            setMessageType('error');
            setShowDropdownMenu(false);
            return;
        }

        try {
            const exportColumns = getExportColumns();
            if (exportColumns.length === 0) {
                setSuccessMessage('No data to export');
                setMessageType('error');
                setShowDropdownMenu(false);
                return;
            }

            const exportData = validData.map((item) => {
                const row = {};
                exportColumns.forEach(({ field, headerName }) => {
                    row[headerName] = formatValueForField(field, item[field]);
                });
                return row;
            });

         
            const totalsRowOut = {};
            exportColumns.forEach(({ field, headerName }, idx) => {
                if (idx === 0) {
                    totalsRowOut[headerName] = 'TOTAL';
                } else if (totalFieldsSet.has(field) && field in displayTotals) {
                    totalsRowOut[headerName] = formatValueForField(field, displayTotals[field]);
                } else {
                    totalsRowOut[headerName] = '';
                }
            });
            exportData.push(totalsRowOut);

            const ws = XLSX.utils.json_to_sheet(exportData);

          
            const colWidths = [];
            Object.keys(exportData[0] || {}).forEach((key, index) => {
                const maxLength = Math.max(
                    key.length,
                    ...exportData.map((row) => {
                        const value = row[key];
                        return value ? String(value).length : 0;
                    })
                );
                colWidths[index] = { wch: Math.min(maxLength + 2, 50) };
            });
            ws['!cols'] = colWidths;

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Lists Details');

            XLSX.writeFile(wb, `list-cost.xlsx`);

            // ✅ SUCCESS MESSAGE
            setSuccessMessage('Export completed successfully!');
            setMessageType('success');
            setShowDropdownMenu(false);

        } catch (error) {
            console.error(error);

            setSuccessMessage('Error exporting data');
            setMessageType('error');
            setShowDropdownMenu(false);
        }
    };

  
    const printToPDF = () => {
        const { rows: validData, scope } = getExportRows();

        if (!validData || validData.length === 0) {
            setSuccessMessage('No data to export');
            setMessageType('error');
            setShowDropdownMenu(false);
            return;
        }

        try {
            const exportColumns = getExportColumns();
            if (exportColumns.length === 0) {
                setSuccessMessage('No data to export');
                setMessageType('error');
                setShowDropdownMenu(false);
                return;
            }

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'pt',
                format: 'a4',
            });

            const pageWidth = doc.internal.pageSize.getWidth();
            const marginX = 20;
            const cardX = marginX;
            const cardY = 20;
            const cardWidth = pageWidth - marginX * 2;

          
            const titleText = 'Lists Costs'.toUpperCase();
         
            const subtitleText = jobCode ? `Job ${jobCode}` : 'LISTS';

            const currentDate = new Date();
            const reportDateStr = `${(currentDate.getMonth() + 1)
                .toString()
                .padStart(2, '0')}/${currentDate.getDate().toString().padStart(2, '0')}/${(currentDate.getFullYear() % 100).toString().padStart(2, '0')}`;

          
            const headerBarHeight = 60;
            const statsBarHeight = 50;
            const detailsBarHeight = 22;
            const cardHeight = headerBarHeight + statsBarHeight + detailsBarHeight;

           
            const BLUE_DARK = [30, 75, 120];
            const BLUE_MID = [46, 111, 159];
            const BLUE_DEEP = [22, 58, 95];

           
            doc.setFillColor(...BLUE_DARK);
            doc.rect(cardX, cardY, cardWidth, headerBarHeight, 'F');

            const logoW = 90;
            doc.setFillColor(...BLUE_DEEP);
            doc.rect(cardX, cardY, logoW, headerBarHeight, 'F');

            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(13);
            doc.text('Rockwell', cardX + logoW / 2, cardY + headerBarHeight / 2 - 2, { align: 'center' });
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(6);
            doc.setTextColor(180, 200, 220);
            doc.text('— SOLUTIONS —', cardX + logoW / 2, cardY + headerBarHeight / 2 + 9, { align: 'center' });

           
            const titleAreaX = cardX + logoW;
            const titleAreaW = cardWidth - logoW;
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(18);
            doc.text(titleText, titleAreaX + titleAreaW / 2, cardY + 28, { align: 'center' });


            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(200, 215, 230);
            doc.text(subtitleText, titleAreaX + titleAreaW / 2, cardY + 44, { align: 'center' });

            
            const metaX = cardX + cardWidth - 12;
            doc.setFontSize(7);
            doc.setTextColor(220, 230, 240);
            doc.setFont('helvetica', 'normal');
            doc.text(`Report: ${reportDateStr}`, metaX, cardY + 16, { align: 'right' });
            
            const statsY = cardY + headerBarHeight;
            doc.setFillColor(...BLUE_MID);
            doc.rect(cardX, statsY, cardWidth, statsBarHeight, 'F');

            const stats = [
                { label: 'CLIENT EST. COST', value: formatCurrency(displayTotals.totalClientEstCost) },
                { label: 'TOTAL EST. COST', value: formatCurrency(displayTotals.totalEstCost) },
                { label: 'TOTAL ACT. COST', value: formatCurrency(displayTotals.totalActCost) },
                { label: 'COST VARIANCE', value: formatCurrency(displayTotals.variance) },
                { label: 'VARIANCE %', value: formatPercent(displayTotals.variancePercent) },
            ];

            const statCellW = cardWidth / stats.length;
            stats.forEach((stat, i) => {
                const cellX = cardX + i * statCellW;

                if (i > 0) {
                    doc.setDrawColor(80, 130, 170);
                    doc.setLineWidth(0.5);
                    doc.line(cellX, statsY + 6, cellX, statsY + statsBarHeight - 6);
                }

                doc.setFont('helvetica', 'normal');
                doc.setFontSize(6.5);
                doc.setTextColor(200, 220, 235);
                doc.text(stat.label, cellX + statCellW / 2, statsY + 15, { align: 'center' });

                doc.setFont('helvetica', 'bold');
                doc.setFontSize(11);
                doc.setTextColor(255, 255, 255);
                doc.text(stat.value, cellX + statCellW / 2, statsY + 35, { align: 'center' });
            });

           
            const detailsY = statsY + statsBarHeight;
            doc.setFillColor(...BLUE_DEEP);
            doc.rect(cardX, detailsY, cardWidth, detailsBarHeight, 'F');

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(255, 255, 255);
            doc.text('LIST DETAILS', cardX + 14, detailsY + detailsBarHeight / 2 + 3);

            const scopeLabel = scope === 'filtered' ? 'Filtered' : 'All';
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7);
            doc.setTextColor(200, 215, 230);
            doc.text(
                `${scopeLabel} Records: ${validData.length}  ·  Confidential`,
                cardX + cardWidth - 12,
                detailsY + detailsBarHeight / 2 + 3,
                { align: 'right' }
            );

          
            const tableStartY = cardY + cardHeight + 10;

            const head = [exportColumns.map((c) => c.headerName)];

            const body = validData.map((item) =>
                exportColumns.map(({ field }) => formatValueForField(field, item[field]))
            );

            const footerRow = exportColumns.map(({ field }, idx) => {
                if (idx === 0) return 'TOTAL';
                if (totalFieldsSet.has(field) && field in displayTotals) {
                    return formatValueForField(field, displayTotals[field]);
                }
                return '';
            });

            const rightAlignedFields = [
                'orderQuantity', 'clientEstimate', 'totalClientEstCost', 'estQuantity',
                'estCost', 'totalEstCost', 'actQuantity', 'actCost', 'totalActCost',
                'variance', 'variancePercent',
            ];
            const centerAlignedFields = [
                'listTypeID', 'clientEstRateType', 'estRateType', 'actRateType', 'status',
            ];

            autoTable(doc, {
                head,
                body,
                foot: [footerRow],
                startY: tableStartY,
                margin: { left: marginX, right: marginX },
                styles: {
                    fontSize: 5,
                    cellPadding: 3,
                    overflow: 'linebreak',
                    valign: 'middle',
                },
                headStyles: {
                    fillColor: [46, 111, 159],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    fontSize: 5,
                },
                footStyles: {
                    fillColor: [240, 248, 255],
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                },
                alternateRowStyles: {
                    fillColor: [250, 250, 250],
                },
                didParseCell: (data) => {
                    if (data.section === 'body' || data.section === 'foot') {
                        const col = exportColumns[data.column.index];
                        if (col) {
                            if (rightAlignedFields.includes(col.field)) {
                                data.cell.styles.halign = 'right';
                            } else if (centerAlignedFields.includes(col.field)) {
                                data.cell.styles.halign = 'center';
                            }
                        }
                    }
                },
                didDrawPage: () => {
                    const pageCount = doc.internal.getNumberOfPages();
                    const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;
                    const pageHeight = doc.internal.pageSize.getHeight();
                    doc.setFontSize(8);
                    doc.setTextColor(120, 120, 120);
                    doc.text(
                        `Page ${pageCurrent} of ${pageCount}`,
                        pageWidth - marginX,
                        pageHeight - 10,
                        { align: 'right' }
                    );
                },
            });

            doc.save(`list-cost-${new Date().toISOString().split('T')[0]}.pdf`);

            setSuccessMessage('Export completed successfully!');
            setMessageType('success');
            setShowDropdownMenu(false);

        } catch (error) {
            console.error('Error exporting to PDF:', error);
            setSuccessMessage('Error exporting data to PDF');
            setMessageType('error');
            setShowDropdownMenu(false);
        }
    };

  
    return (

        <>
            <div className='main-container-postage-details'>
                {(loading || savingChanges) && (
                    <AisScreenLoader />
                )}

                <div className="mail-container-postage">


                    <div className="job-details-breadcrumb px-0 pb-4">
                        <nav className="breadcrumb-nav">
                          

                            <ol className="breadcrumb-list">


                                {showReportsCrumb && (
                                    <>
                                        <li className="breadcrumb-item">
                                            <a
                                                href="/reports"
                                                className="breadcrumb-link"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    navigate('/reports', {
                                                        state: {
                                                            restoreReports: true,
                                                            reportFilters: reportsContext.reportFilters || {},
                                                            selectedClientId: reportsContext.selectedClientId || [],
                                                            reportType: reportsContext.reportType || '',
                                                            rowData: reportsContext.rowData || []
                                                        }
                                                    });
                                                }}
                                            >
                                                Reports
                                            </a>
                                        </li>
                                        <li className="breadcrumb-separator">/</li>
                                    </>
                                )}

                              
                                {showClientSelectCrumb && (
                                    <>
                                        <li className="breadcrumb-item">
                                            <Icon
                                                name="user-icon"
                                                size={14}
                                                alt="user-icon"
                                                className="breadcrumb-icon"
                                            />
                                            <a
                                                href="/dashboard"
                                                className="breadcrumb-link"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    sessionStorage.removeItem('jobDetailsSource');
                                                    sessionStorage.removeItem('isAllClientsFlow');
                                                    navigate('/dashboard');
                                                }}
                                            >
                                                Client Select
                                            </a>
                                        </li>
                                        <li className="breadcrumb-separator">/</li>
                                    </>
                                )}

                              
                                {showClientNameCrumb && (
                                    <>
                                        <li className="breadcrumb-item">
                                            <a
                                                href="/client-dashboard"
                                                className="breadcrumb-link"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    navigate('/client-dashboard', {
                                                        state: {
                                                            clientId,
                                                            source: 'client-dashboard',
                                                            isAllClients: false,
                                                        },
                                                    });
                                                }}
                                            >
                                                Client: {clientName}
                                            </a>
                                        </li>
                                        <li className="breadcrumb-separator">/</li>
                                    </>
                                )}

                              
                                {navigationSource === 'job-module' && !showClientSelectCrumb && (
                                    <>
                                        <li className="breadcrumb-item d-flex align-items-center">
                                            <span style={{ paddingRight: '4px' }}>
                                                <Icon name="mail-icon" size={14} />
                                            </span>
                                            <a
                                                href="/job-module"
                                                className="breadcrumb-link"
                                                onClick={handleJobListsClick}
                                            >
                                                Job Lists
                                            </a>
                                        </li>
                                        <li className="breadcrumb-separator">/</li>
                                    </>
                                )}

                             
                                {cameFromIntegration && navigationSource === 'client-dashboard' && (
                                    <>
                                        <li className="breadcrumb-item">
                                            <a
                                                href="#"
                                                className="breadcrumb-link"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    navigate('/talliesSection', {
                                                        state: {
                                                            activeTab: 'lists',
                                                            clientId: clientId,
                                                            clientName: clientName
                                                        }
                                                    });
                                                }}
                                            >
                                                Integration
                                            </a>
                                        </li>
                                        <li className="breadcrumb-separator">/</li>
                                    </>
                                )}

                                {navigationSource === 'list-upload' && (
                                    <>
                                        <li className="breadcrumb-item">
                                            <a
                                                href="#"
                                                className="breadcrumb-link"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    sessionStorage.removeItem('jobDetailsSource');
                                                    sessionStorage.removeItem('isAllClientsFlow');
                                                    navigate('/talliesSection', {
                                                        state: {
                                                            activeTab: 'lists'
                                                        }
                                                    });
                                                }}
                                            >
                                                Integration
                                            </a>
                                        </li>
                                        <li className="breadcrumb-separator">/</li>
                                    </>
                                )}

                                {/* ✅ Job Details */}
                                <li className="breadcrumb-item">
                                    <a
                                        href={`/job-details/${activeJobId}`}
                                        className="breadcrumb-link"
                                        onClick={handleJobDetailsClick}
                                    >
                                        Job Details: {jobCode}
                                    </a>
                                </li>

                                <li className="breadcrumb-separator">/</li>

                                <li className="breadcrumb-item dropdown-container" style={{ position: 'relative' }}>
                                    <div
                                        className="breadcrumb-dropdown-trigger"
                                        onClick={() => setShowCostTypeDropdown(!showCostTypeDropdown)}
                                        style={{
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '5px',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            backgroundColor: showCostTypeDropdown ? '#e9ecef' : 'transparent'
                                        }}
                                    >
                                        <span className="active">
                                            {selectedCostType === 'postage' && 'Postage'}
                                            {selectedCostType === 'package' && 'Package Costs'}
                                            {selectedCostType === 'backend' && 'Backend Costs'}
                                            {selectedCostType === 'lists' && 'Lists'}
                                        </span>
                                        <Icon name="expand-open" size={16} alt="dropdown-icon" />
                                    </div>

                                    {showCostTypeDropdown && (
                                        <div className="cost-type-dropdown-menu" style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: '0',
                                            backgroundColor: 'white',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                            zIndex: 1000,
                                            minWidth: '150px'
                                        }}>
                                            {[
                                                { key: 'postage', label: 'Postage' },
                                                { key: 'package', label: 'Package Costs' },
                                                { key: 'backend', label: 'Backend Costs' },
                                                { key: 'lists', label: 'Lists' },
                                            ].map(({ key, label }, idx, arr) => (
                                                <div
                                                    key={key}
                                                    className="dropdown-option"
                                                    onClick={() => handleCostTypeChange(key)}
                                                    style={{
                                                        padding: '8px 12px',
                                                        cursor: 'pointer',
                                                        backgroundColor: selectedCostType === key ? '#f0f8ff' : 'white',
                                                        borderBottom: idx < arr.length - 1 ? '1px solid #eee' : 'none'
                                                    }}
                                                >
                                                    {label}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </li>

                            </ol>
                        </nav>
                    </div>

                    {/* Page Title */}
                    <h1 className="job-details-title pb-4">
                        {jobCode} Lists
                    </h1>

                    {error && (
                        <div style={{
                            backgroundColor: '#f8d7da',
                            color: '#721c24',
                            padding: '10px 15px',
                            borderRadius: '4px',
                            marginBottom: '15px',
                            border: '1px solid #f5c6cb'
                        }}>
                            {error}
                        </div>
                    )}
                    {/* Grid Section */}
                    <div className="postage-grid-container">
                        <div className='d-flex justify-content-between align-items-center mb-4'>
                            {!isEditMode ? (
                                <button className='manage-components-button' onClick={handleManageComponents}>
                                    Manage Components
                                </button>
                            ) : (
                                <div style={{ width: '120px' }}></div>
                            )}

                            <div className="d-flex gap-2 align-items-center">
                                {isEditMode && (
                                    <>
                                        <button
                                            className="save-changes-btn"
                                            onClick={handleSaveChanges}
                                            disabled={savingChanges}
                                        >
                                            {savingChanges ? 'Saving...' : 'Save Changes'}
                                        </button>
                                        <button
                                            className="cancel-btn btn-sm"
                                            onClick={handleCancelChanges}
                                            disabled={savingChanges}
                                        >
                                            Cancel
                                        </button>
                                    </>
                                )}

                                <div className="export-button-wrapper mb-0" ref={menuDropdownRef}>
                                    <button className="job-details-menu-buttons" onClick={toggleDropdownMenu}>
                                        <span className="menu-dot"></span>
                                        <span className="menu-dot"></span>
                                        <span className="menu-dot"></span>
                                    </button>

                                    {showDropdownMenu && (
                                        <div className="dropdown-menu-container" style={{ overflow: 'hidden' }}>
                                            {/* Export to Excel */}
                                            <button
                                                className="dropdown-menu-item"
                                                onClick={() => {
                                                    exportToExcel();
                                                    setShowDropdownMenu(false);
                                                }}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: '8px'
                                                }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                    <polyline points="14 2 14 8 20 8" />
                                                    <line x1="8" y1="13" x2="16" y2="13" />
                                                    <line x1="8" y1="17" x2="16" y2="17" />
                                                    <polyline points="10 9 9 9 8 9" />
                                                </svg>
                                                Export to Excel
                                            </button>

                                            {/* Print To Pdf */}
                                            <button
                                                className="dropdown-menu-item"
                                                onClick={() => {
                                                    printToPDF();
                                                    setShowDropdownMenu(false);
                                                }}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: '8px'
                                                }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="6 9 6 2 18 2 18 9" />
                                                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                                                    <rect x="6" y="14" width="12" height="8" />
                                                </svg>
                                                Print To PDF
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <AgGrid
                            ref={agGridRef}
                            rowData={listsData}
                            pinnedBottomRowData={[totalsRow]}
                            columnDefs={columnDefs}
                            hideExportButton={true}
                            hideSideBar={true}
                            hideSelectAll={true}
                            height="500px"
                            loading={loading}
                            enableSorting={true}
                            enableFilter={true}
                            searchPlaceholder="Search list items..."
                            noRowsMessage={loading ? 'Loading...' : 'No list records found'}
                            noFilteredRowsMessage="No matching list records found"
                            onGridReady={(params) => { gridApiRef.current = params.api; }}
                            onFilterChanged={handleFilterChanged}
                            defaultColDef={{
                                resizable: true,
                                sortable: true,
                                filter: true,
                                editable: false,
                            }}
                            gridOptions={{
                                getRowId: getRowId,
                                tooltipShowDelay: 0,

                                tooltipHideDelay: 1000,
                                getRowStyle: (params) => {
                                    if (params.data?.isTotalsRow) {
                                        return { backgroundColor: '#f0f8ff', fontWeight: 'bold' };
                                    }
                                    return null;
                                },
                            }}
                        />
                    </div>
                </div>
            </div>

            {successMessage && (
                <SuccessMessage
                    message={successMessage}
                    type={messageType}

                    onClose={() => {
                        setSuccessMessage('');
                        setMessageType('success');
                    }}

                />
            )}
        </>
    );
};

export default ListsDetails;
